import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Heading, Field, SubmitButton, ErrorNote, PasswordHint, ProviderButtons } from "@/components/AuthUI";
import { getPasswordValidationError } from "@/lib/password";

export const AuthPage = ({ initialMode = "login" }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin) {
      const passwordError = getPasswordValidationError(password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
    }
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, "");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell homeTestId="auth-home-link">
      <Heading
        title={isLogin ? "Welcome back" : "Create your workspace"}
        sub={isLogin ? "Sign in to your workspace." : "Free plan, no credit card. Add your first document in about a minute."}
      />
      <form onSubmit={submit} className="flex flex-col gap-6">
        <Field
          label="Email"
          id="email"
          testid="email-input"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={setEmail}
          required
        />
        <Field
          label="Password"
          id="password"
          testid="password-input"
          type="password"
          placeholder={isLogin ? "Your password" : "At least 8 characters"}
          value={password}
          onChange={setPassword}
          required
          minLength={isLogin ? undefined : 8}
        />
        {!isLogin && <PasswordHint />}
        {error && <ErrorNote testid="auth-error">{error}</ErrorNote>}
        <SubmitButton testid="auth-submit-button" loading={loading}>
          {isLogin ? "Sign in" : "Create account"}
        </SubmitButton>
      </form>
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.12em] text-soft">
        <span className="h-px flex-1 bg-input" />
        <span>or</span>
        <span className="h-px flex-1 bg-input" />
      </div>
      <ProviderButtons onSelect={(provider) => setError(`${provider} sign-in is not configured yet.`)} />
      <div className="flex justify-between gap-4 text-[15px] text-soft">
        <span>
          {isLogin ? "New here?" : "Already have an account?"}{" "}
          <button
            data-testid="toggle-auth-mode"
            type="button"
            onClick={() => {
              setMode(isLogin ? "register" : "login");
              setError("");
            }}
            className="text-foreground font-semibold underline"
          >
            {isLogin ? "Create account" : "Sign in"}
          </button>
        </span>
        <Link to="/forgot-password" data-testid="forgot-password-link" className="underline">
          Forgot password?
        </Link>
      </div>
    </AuthShell>
  );
};
